import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Settings, 
  Mail, 
  Server, 
  Lock, 
  User, 
  Hash, 
  Shield, 
  Eye, 
  EyeOff, 
  Save, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Loader2
} from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const { token, admin } = useAdminAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Arcano Projetos',
    use_ssl: true,
    frontend_url: ''
  });
  
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Redirect if not super admin
  useEffect(() => {
    if (admin && !admin.is_super_admin) {
      navigate('/admin/dashboard');
    }
  }, [admin, navigate]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 465,
        smtp_user: response.data.smtp_user || '',
        smtp_password: '', // Don't show password
        from_email: response.data.from_email || '',
        from_name: response.data.from_name || 'Arcano Projetos',
        use_ssl: response.data.use_ssl !== false,
        frontend_url: response.data.frontend_url || ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.put(
        `${API}/admin/settings/email`,
        {
          smtp_host: settings.smtp_host,
          smtp_port: parseInt(settings.smtp_port),
          smtp_user: settings.smtp_user,
          smtp_password: settings.smtp_password || null,
          from_email: settings.from_email,
          from_name: settings.from_name,
          use_ssl: settings.use_ssl,
          frontend_url: settings.frontend_url || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
      setSettings(prev => ({ ...prev, smtp_password: '' })); // Clear password field after save
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao salvar configurações' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Digite um email para teste' });
      return;
    }

    setSendingTest(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(
        `${API}/admin/settings/email/test`,
        { to_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: response.data.message });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao enviar email de teste' 
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (!admin?.is_super_admin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-arcano-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="text-arcano-blue" />
          Configurações
        </h1>
        <p className="text-gray-600 text-sm md:text-base">Gerencie as configurações do sistema</p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          {message.text}
        </div>
      )}

      {/* Email Settings Card */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 md:p-6 border-b border-gray-200">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="text-arcano-blue" />
            Servidor de Email (SMTP)
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Configure o servidor SMTP para envio de emails do sistema
          </p>
        </div>

        <form onSubmit={handleSave} className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Server Settings */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Server size={16} className="inline mr-2" />
                Servidor SMTP (Host)
              </label>
              <input
                type="text"
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="mail.exemplo.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                required
                data-testid="smtp-host-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Hash size={16} className="inline mr-2" />
                Porta
              </label>
              <input
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                placeholder="465"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                required
                data-testid="smtp-port-input"
              />
              <p className="text-xs text-gray-500 mt-1">Comum: 465 (SSL) ou 587 (TLS)</p>
            </div>
          </div>

          {/* Authentication */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User size={16} className="inline mr-2" />
                Usuário SMTP
              </label>
              <input
                type="text"
                value={settings.smtp_user}
                onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                placeholder="noreply@exemplo.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                required
                data-testid="smtp-user-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock size={16} className="inline mr-2" />
                Senha SMTP
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent pr-10"
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Deixe em branco para manter a senha atual</p>
            </div>
          </div>

          {/* From Settings */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail size={16} className="inline mr-2" />
                Email de Origem (From)
              </label>
              <input
                type="email"
                value={settings.from_email}
                onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                placeholder="noreply@exemplo.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                required
                data-testid="from-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User size={16} className="inline mr-2" />
                Nome de Exibição
              </label>
              <input
                type="text"
                value={settings.from_name}
                onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                placeholder="Arcano Projetos"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                required
                data-testid="from-name-input"
              />
            </div>
          </div>

          {/* Frontend URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Server size={16} className="inline mr-2" />
              URL do Site (para links nos emails)
            </label>
            <input
              type="url"
              value={settings.frontend_url}
              onChange={(e) => setSettings({ ...settings, frontend_url: e.target.value })}
              placeholder="https://arcanoprojetos.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
              data-testid="frontend-url-input"
            />
            <p className="text-xs text-gray-500 mt-1">URL usada nos links de redefinição de senha e primeiro acesso</p>
          </div>

          {/* SSL Toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.use_ssl}
                onChange={(e) => setSettings({ ...settings, use_ssl: e.target.checked })}
                className="sr-only peer"
                data-testid="use-ssl-checkbox"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-arcano-blue/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-arcano-blue"></div>
            </label>
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Shield size={16} />
              Usar SSL/TLS
            </span>
            <span className="text-xs text-gray-500">(Recomendado para porta 465)</span>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-arcano-blue text-white px-4 md:px-6 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50 w-full md:w-auto justify-center text-sm md:text-base"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </form>
      </div>

      {/* Test Email Card */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 md:p-6 border-b border-gray-200">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Send className="text-arcano-blue" />
            Enviar Email de Teste
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Verifique se as configurações estão funcionando corretamente
          </p>
        </div>

        <div className="p-4 md:p-6">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <div className="flex-1">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Digite o email para teste..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent text-sm md:text-base"
                data-testid="test-email-input"
              />
            </div>
            <button
              type="button"
              onClick={handleSendTest}
              disabled={sendingTest || !testEmail}
              className="flex items-center gap-2 bg-green-600 text-white px-4 md:px-6 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 justify-center text-sm md:text-base"
              data-testid="send-test-btn"
            >
              {sendingTest ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              {sendingTest ? 'Enviando...' : 'Enviar Teste'}
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle size={20} className="text-blue-500 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium">Dicas de configuração</p>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li>Para Hostgator: use porta 465 com SSL ativado</li>
            <li>Para Gmail: use porta 587 com SSL desativado (usa STARTTLS)</li>
            <li>Certifique-se de que SPF, DKIM e DMARC estão configurados no DNS</li>
            <li>Emails para Hotmail/Outlook podem demorar alguns minutos</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
